import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { DISCOUNT_TYPES } from "../../api/assessmentCoupons";
import { fromLocalInput, toLocalInput } from "./couponFormat";
import { TARGET_TYPES, buildPickerOptions, targetTypeLabel } from "./couponTargets";

const ACCENT = "#17479e";

const primaryButtonSx = {
  textTransform: "none",
  fontWeight: 700,
  borderRadius: 2,
  background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
  "&:hover": { background: "linear-gradient(135deg, #123a80 0%, #0d47a1 100%)" },
  "&.Mui-disabled": { background: "#b0bec5" },
};

const cancelButtonSx = {
  flex: "0 0 auto",
  textTransform: "none",
  fontWeight: 600,
  borderRadius: 2,
  color: "#555",
  backgroundColor: "#eceff1",
  "&:hover": { backgroundColor: "#e0e3e6" },
};

const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

function DialogHeader({ icon, title, subtitle, onClose }) {
  return (
    <Box
      sx={{
        position: "relative",
        p: 3,
        background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
        color: "#fff",
      }}
    >
      <IconButton
        onClick={onClose}
        size="small"
        sx={{ position: "absolute", top: 10, right: 10, color: "rgba(255,255,255,0.85)" }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 44,
            height: 44,
            flexShrink: 0,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.18)",
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1.15rem" }}>{title}</Typography>
          {subtitle && <Typography sx={{ fontSize: "0.78rem", opacity: 0.85 }}>{subtitle}</Typography>}
        </Box>
      </Stack>
    </Box>
  );
}

DialogHeader.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

// Create or edit a coupon. On create the admin chooses what it applies to (a course or a test series) and
// picks the item; on edit the target is shown read-only because a coupon's target never changes.
// `courses` and `series` are the full lists loaded by the screen; the picker narrows them to what can be bought.
function AssessmentCouponForm({ open, coupon, courses, series, onClose, onSave }) {
  const [targetType, setTargetType] = useState("CATEGORY");
  const [target, setTarget] = useState(null);
  const [code, setCode] = useState("");
  const [codeTouched, setCodeTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [discountType, setDiscountType] = useState("PERCENTAGE");
  const [discountValue, setDiscountValue] = useState("");
  const [valueTouched, setValueTouched] = useState(false);
  const [maxDiscount, setMaxDiscount] = useState("");
  const [usageLimit, setUsageLimit] = useState("");
  const [perUserLimit, setPerUserLimit] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState("ACTIVE");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      return;
    }
    setTargetType("CATEGORY");
    setTarget(null);
    setCodeTouched(false);
    setValueTouched(false);
    setCode(coupon?.code || "");
    setDescription(coupon?.description || "");
    setDiscountType(coupon?.discountType || "PERCENTAGE");
    setDiscountValue(coupon?.discountValue ?? "");
    setMaxDiscount(coupon?.maxDiscountAmount ?? "");
    setUsageLimit(coupon?.usageLimit ?? "");
    setPerUserLimit(coupon?.perUserUsageLimit ?? "");
    setStartDate(toLocalInput(coupon?.startDate));
    setEndDate(toLocalInput(coupon?.endDate));
    setStatus(coupon?.status || "ACTIVE");
  }, [open, coupon]);

  const pickerOptions = useMemo(
    () => buildPickerOptions(targetType, courses, series),
    [targetType, courses, series],
  );

  const isPercentage = discountType === "PERCENTAGE";
  const valueNumber = Number(discountValue);
  const valueInvalid =
    discountValue === "" || !(valueNumber > 0) || (isPercentage && valueNumber > 100);
  const valueMessage = (() => {
    if (!valueTouched) return undefined;
    if (discountValue === "") return "Please fill out this field";
    if (!(valueNumber > 0)) return "Must be greater than 0";
    if (isPercentage && valueNumber > 100) return "Percentage can't exceed 100";
    return undefined;
  })();

  const maxInvalid = isPercentage && maxDiscount !== "" && !(Number(maxDiscount) > 0);
  const intInvalid = (v) => v !== "" && !(Number.isInteger(Number(v)) && Number(v) >= 1);
  const usageInvalid = intInvalid(usageLimit);
  const perUserInvalid = intInvalid(perUserLimit);
  const datesInvalid =
    startDate && endDate && new Date(endDate).getTime() <= new Date(startDate).getTime();

  const isValid =
    (Boolean(coupon) || Boolean(target)) &&
    code.trim().length > 0 &&
    !valueInvalid &&
    !maxInvalid &&
    !usageInvalid &&
    !perUserInvalid &&
    !datesInvalid;

  const toNumber = (v) => (v === "" || v == null ? undefined : Number(v));

  // A new coupon carries exactly one target; an edit never sends one.
  const targetPayload = () => {
    if (coupon) return {};
    return targetType === "CATEGORY" ? { categoryId: target.id } : { seriesId: target.id };
  };

  const handleSubmit = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    try {
      await onSave({
        id: coupon?.id,
        payload: {
          ...targetPayload(),
          code: code.trim().toUpperCase(),
          // Always sent (even blank) so an edit can clear a description.
          description: description.trim(),
          discountType,
          discountValue: Number(discountValue),
          maxDiscountAmount: isPercentage ? toNumber(maxDiscount) : undefined,
          usageLimit: toNumber(usageLimit),
          perUserUsageLimit: toNumber(perUserLimit),
          startDate: fromLocalInput(startDate),
          endDate: fromLocalInput(endDate),
          active: status === "ACTIVE",
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: "#f7f8fb", overflow: "hidden" } }}
    >
      <DialogHeader
        icon={<LocalOfferOutlinedIcon />}
        title={coupon ? "Edit Coupon" : "Add Coupon"}
        subtitle={coupon ? "Update discount, limits and validity" : "Set up a discount code"}
        onClose={onClose}
      />
      <DialogContent sx={{ pt: 3, backgroundColor: "#f7f8fb" }}>
        <Card sx={{ boxShadow: 1, borderRadius: 3, overflow: "hidden" }}>
          <CardContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.25 }}>
            {coupon ? (
              <TextField
                label="Applies to"
                fullWidth
                size="small"
                disabled
                value={`${targetTypeLabel(coupon.targetType)} — ${coupon.targetName ?? "(no longer exists)"}`}
                helperText="A coupon's course or test series can't be changed after it is created"
                sx={fieldSx}
              />
            ) : (
              <>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Applies to</InputLabel>
                  <Select
                    label="Applies to *"
                    value={targetType}
                    onChange={(e) => {
                      setTargetType(e.target.value);
                      setTarget(null);
                    }}
                  >
                    {TARGET_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Autocomplete
                  size="small"
                  fullWidth
                  options={pickerOptions}
                  value={target}
                  onChange={(_, value) => setTarget(value)}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  noOptionsText={
                    targetType === "CATEGORY"
                      ? "No premium, active courses available"
                      : "No premium test series available"
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label={targetType === "CATEGORY" ? "Course" : "Test Series"}
                      sx={fieldSx}
                    />
                  )}
                />
              </>
            )}
            <TextField
              label="Coupon Code"
              fullWidth
              size="small"
              required
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/\s/g, ""))}
              onBlur={() => setCodeTouched(true)}
              error={codeTouched && !code.trim()}
              helperText={
                codeTouched && !code.trim()
                  ? "Please fill out this field"
                  : "Must be unique across all coupons. Students can enter it in any case."
              }
              inputProps={{ maxLength: 50 }}
              sx={fieldSx}
            />
            <TextField
              label="Description (optional)"
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              helperText={`${description.length}/500`}
              inputProps={{ maxLength: 500 }}
              sx={fieldSx}
            />
            <FormControl fullWidth size="small" required>
              <InputLabel>Discount Type</InputLabel>
              <Select
                label="Discount Type *"
                value={discountType}
                onChange={(e) => setDiscountType(e.target.value)}
              >
                {DISCOUNT_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Discount Value"
              type="number"
              fullWidth
              size="small"
              required
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
              onBlur={() => setValueTouched(true)}
              error={Boolean(valueMessage)}
              helperText={valueMessage ?? (isPercentage ? "Percent off the price (1–100)" : "Rupees off the price")}
              inputProps={{ min: 0, step: "any" }}
              sx={fieldSx}
            />
            {isPercentage && (
              <TextField
                label="Max Discount Amount (optional)"
                type="number"
                fullWidth
                size="small"
                value={maxDiscount}
                onChange={(e) => setMaxDiscount(e.target.value)}
                error={maxInvalid}
                helperText={maxInvalid ? "Must be greater than 0" : "Caps the rupee discount, in ₹"}
                inputProps={{ min: 0, step: "any" }}
                sx={fieldSx}
              />
            )}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <TextField
                label="Usage Limit (optional)"
                type="number"
                fullWidth
                size="small"
                value={usageLimit}
                onChange={(e) => setUsageLimit(e.target.value)}
                error={usageInvalid}
                helperText={usageInvalid ? "Whole number, at least 1" : "Total redemptions"}
                inputProps={{ min: 1, step: 1 }}
                sx={fieldSx}
              />
              <TextField
                label="Per-User Usage Limit (optional)"
                type="number"
                fullWidth
                size="small"
                value={perUserLimit}
                onChange={(e) => setPerUserLimit(e.target.value)}
                error={perUserInvalid}
                helperText={perUserInvalid ? "Whole number, at least 1" : "Redemptions per student"}
                inputProps={{ min: 1, step: 1 }}
                sx={fieldSx}
              />
              <TextField
                label="Start Date (optional)"
                type="datetime-local"
                fullWidth
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={fieldSx}
              />
              <TextField
                label="End Date (optional)"
                type="datetime-local"
                fullWidth
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                error={Boolean(datesInvalid)}
                helperText={datesInvalid ? "Must be after the start date" : undefined}
                InputLabelProps={{ shrink: true }}
                sx={fieldSx}
              />
            </Box>
            {coupon && (
              <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
                Optional fields that are already set can be changed but not cleared.
              </Typography>
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 1.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: status === "ACTIVE" ? "#2e7d32" : "divider",
                backgroundColor: status === "ACTIVE" ? "#eaf7ec" : "#f5f7fb",
              }}
            >
              <Box>
                <Typography
                  sx={{ fontWeight: 600, fontSize: "0.85rem", color: status === "ACTIVE" ? "#2e7d32" : "#555" }}
                >
                  {status === "ACTIVE" ? "Active" : "Inactive"}
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                  {status === "ACTIVE" ? "Students can redeem this code" : "Code is rejected at checkout"}
                </Typography>
              </Box>
              <Switch
                checked={status === "ACTIVE"}
                onChange={(e) => setStatus(e.target.checked ? "ACTIVE" : "INACTIVE")}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#2e7d32" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#2e7d32" },
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, backgroundColor: "#f7f8fb" }}>
        <Box sx={{ display: "flex", width: "100%", gap: 1.5 }}>
          <Button onClick={onClose} disabled={submitting} sx={cancelButtonSx}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            disableElevation
            loading={submitting}
            onClick={handleSubmit}
            disabled={!isValid}
            sx={{ flex: 1, ...primaryButtonSx }}
          >
            {coupon ? "Save Changes" : "Create Coupon"}
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

AssessmentCouponForm.propTypes = {
  open: PropTypes.bool.isRequired,
  coupon: PropTypes.object,
  courses: PropTypes.array.isRequired,
  series: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default AssessmentCouponForm;
